<template>
  <div v-if="hasPhysicalProducts" class="round-up-selector">
    <div class="text-heading-small mb-12">
      {{ $lang('checkout', 'donation_heading') || 'Vill du ge extra stöd?' }}
    </div>
    <div class="text-small mb-16">
      {{ $lang('checkout', 'donation_description') || 'Ge extra stöd till föreningen. Hela beloppet går till säljaren.' }}
    </div>

    <div class="grid grid-cols-3 gap-12 mb-12">
      <CheckboxComponent
        :highlight="roundUpMode === 'amount1'"
        :is-radio="true"
        :checked="roundUpMode === 'amount1'"
        :text="amount1Text"
        @on-check="selectRoundUpMode('amount1')"
      />
      <CheckboxComponent
        :highlight="roundUpMode === 'amount2'"
        :is-radio="true"
        :checked="roundUpMode === 'amount2'"
        :text="amount2Text"
        @on-check="selectRoundUpMode('amount2')"
      />
      <CheckboxComponent
        :highlight="roundUpMode === 'custom'"
        :is-radio="true"
        :checked="roundUpMode === 'custom'"
        :text="$lang('checkout', 'donation_custom') || 'Eget belopp'"
        @on-check="selectRoundUpMode('custom')"
      />
    </div>

    <div v-if="roundUpMode === 'custom'" class="mb-12">
      <div class="flex gap-12">
        <TextInput
          v-model="customAmount"
          type="number"
          :placeholder="customAmountPlaceholder"
          :min="1"
          :max="maxDonation"
          class="flex-1"
        />
        <ButtonComponent
          :text="$lang('checkout', 'donation_apply') || 'Lägg till'"
          @clicked="applyCustomAmount"
        />
      </div>
    </div>

    <div v-if="roundUpMode === 'none'" class="text-small text-grey mb-12">
      {{ $lang('checkout', 'round_up_none_selected') || 'Välj ett alternativ för att runda upp' }}
    </div>

    <div v-if="roundUpAmount > 0" class="flex justify-between items-center p-12 bg-secondary rounded">
      <span class="text-small">{{ $lang('checkout', 'round_up_amount') || 'Avrundning' }}:</span>
      <span class="text-base-medium text-primary">+{{ formatPrice(roundUpAmount) }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import CheckboxComponent from '~/components/forms/CheckboxComponent.vue';
import TextInput from '~/components/forms/TextInput.vue';
import ButtonComponent from '~/components/forms/ButtonComponent.vue';
import { useCartStore } from '~/store/cart';
import { useConfigStore } from '~/store/config';
import { formatPriceUnit } from '~/util/price-formatting';

const cartStore = useCartStore();
const config = useConfigStore();
const { $lang } = useNuxtApp();

const emit = defineEmits<{
  // No events needed - component manages cart directly
}>();

type RoundUpMode = 'none' | 'amount1' | 'amount2' | 'custom';

const roundUpMode = ref<RoundUpMode>('none');
const customAmount = ref<string>('');
const roundUpAmount = ref(0);

// Currency-dependent amounts based on cart currency
const isEuro = computed(() => cartStore.cartData.currency === 'EUR');
const currencySymbol = computed(() => isEuro.value ? '€' : 'kr');

const amount1Value = computed(() => isEuro.value ? 1 : 10);
const amount2Value = computed(() => isEuro.value ? 5 : 50);

// Maximum donation amounts
const maxDonation = computed(() => isEuro.value ? 50 : 500);

const amount1Text = computed(() => `${amount1Value.value} ${currencySymbol.value}`);
const amount2Text = computed(() => `${amount2Value.value} ${currencySymbol.value}`);
const customAmountPlaceholder = computed(() => 
  `${$lang('checkout', 'donation_custom_placeholder') || 'Ange belopp'} (${currencySymbol.value})`
);

// Check if there are any physical products (not gift certificates, not donations)
const hasPhysicalProducts = computed(() => {
  const items = cartStore.cartData.items || [];
  return items.some(item => 
    !item.isGiftCertificate && 
    item.product?.pr_donation_value !== '1'
  );
});

const formatPrice = (amount: number) => {
  return formatPriceUnit(amount, config.market.country);
};

const selectRoundUpMode = (mode: RoundUpMode) => {
  roundUpMode.value = mode;
  
  if (mode === 'amount1') {
    roundUpAmount.value = amount1Value.value;
    updateRoundUp(roundUpAmount.value);
  } else if (mode === 'amount2') {
    roundUpAmount.value = amount2Value.value;
    updateRoundUp(roundUpAmount.value);
  } else if (mode === 'custom') {
    // Don't update yet, wait for user to enter amount and click button
    roundUpAmount.value = 0;
  } else {
    roundUpAmount.value = 0;
    updateRoundUp(0);
  }
};

const applyCustomAmount = async () => {
  const amount = parseFloat(customAmount.value) || 0;
  
  // Validate maximum
  if (amount > maxDonation.value) {
    const message = $lang('checkout', 'donation_max_exceeded', { max: maxDonation.value })
      || `Maximal donation är ${maxDonation.value} ${currencySymbol.value}`;
    alert(message);
    customAmount.value = String(maxDonation.value);
    roundUpAmount.value = maxDonation.value;
  } else {
    roundUpAmount.value = Math.max(0, Math.floor(amount));
    // Update input field to show actual added amount (integer only)
    customAmount.value = String(roundUpAmount.value);
  }
  
  await updateRoundUp(roundUpAmount.value);
};

const updateRoundUp = async (amount: number) => {
  await cartStore.updateRoundUpProduct(amount);
};

// No need to watch cart total changes since we're using fixed amounts now

// Watch for physical products - remove donation if no physical products remain
watch(hasPhysicalProducts, (hasPhysical) => {
  if (!hasPhysical && roundUpAmount.value > 0) {
    roundUpMode.value = 'none';
    roundUpAmount.value = 0;
    cartStore.removeRoundUpProduct();
  }
});

// Clean up on unmount
onUnmounted(() => {
  if (roundUpAmount.value > 0) {
    cartStore.removeRoundUpProduct();
  }
});
</script>

<style scoped lang="postcss">
.round-up-selector {
  @apply w-full;
}
</style>
